import React, { useState } from 'react';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchVariantHistoryDownload, useVariantHistory } from '@/hooks/useVariantHistory';
import { isYmd, ptYesterday } from '@/shared/ptDates';
import {
  DEFAULT_VARIANT_HISTORY_PERIOD,
  isVariantHistoryPeriod,
  type VariantHistoryPeriod,
} from '@/shared/variantHistoryPeriods';
import type { VariantHistoryRow } from '@/shared/types/variantHistory';
import { VariantHistoryControls } from './VariantHistoryControls';
import { VariantHistoryTable } from './VariantHistoryTable';
import { formatSnapshotDate, pageItems } from './variantHistoryFormat';

// Merchant › Reports › Variant History (FIG-239 · UC-256, FIG-430). One row per PT day, newest first, read from the
// stored daily snapshots through variant-history-data-v1 (admin-only on the server) and never recalculated. A row
// downloads that day's Variants CSV, which the server builds from the stored variant rows and logs in User Activity.
// The tab itself is only offered to admins (OrganisationReportsTab); the server is what enforces it.

interface VariantHistoryTabProps {
  organisationId: string;
}

/** Saves CSV text as a file through the browser. */
function saveCsv(fileName: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

export const VariantHistoryTab: React.FC<VariantHistoryTabProps> = ({ organisationId }) => {
  const [period, setPeriod] = useState<VariantHistoryPeriod>(DEFAULT_VARIANT_HISTORY_PERIOD);
  // What is in the Go to date box (picked or typed). Only a complete, selectable date is applied.
  const [dateInput, setDateInput] = useState('');
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Yesterday in PT is the latest day that exists; the browser's own zone plays no part. Read on every render, so a
  // tab left open across PT midnight moves on (the server refuses today and later in any case).
  const latest = ptYesterday();
  const dateIsValid = isYmd(dateInput);
  const dateIsTooLate = dateIsValid && dateInput > latest;
  const appliedDate = dateIsValid && !dateIsTooLate ? dateInput : '';

  const query = useVariantHistory({ organisationId, period, date: appliedDate, page });
  const data = query.data;

  const changePeriod = (value: string) => {
    if (!isVariantHistoryPeriod(value)) return;
    setPeriod(value);
    setPage(1);
  };
  const changeDate = (value: string) => {
    setDateInput(value);
    setPage(1);
  };
  const clearDate = () => {
    setDateInput('');
    setPage(1);
  };

  const download = async (row: VariantHistoryRow) => {
    if (row.status !== 'recorded' || downloading) return;
    setDownloading(row.snapshotDate);
    setDownloaded(null);
    setDownloadError(null);
    try {
      const file = await fetchVariantHistoryDownload(organisationId, row.snapshotDate);
      saveCsv(file.fileName, file.csv);
      setDownloaded(file.fileName);
    } catch {
      setDownloadError(`Couldn't download the file for ${formatSnapshotDate(row.snapshotDate)}. No file was saved.`);
    } finally {
      setDownloading(null);
    }
  };

  const entries = data?.totalEntries ?? 0;
  const isEmpty = !!data && data.rows.length === 0;
  const emptyText = appliedDate
    ? 'No snapshot for this date'
    : data?.firstSnapshotDate === null
      ? 'No snapshots have been recorded for this merchant yet.'
      : 'No snapshots in this period.';

  return (
    <div className="space-y-4" data-testid="variant-history-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-gray-900">Variant History</h3>
        {downloaded && (
          <span role="status" className="inline-flex items-center gap-1.5 text-sm text-green-700">
            <CheckCircle className="h-4 w-4" aria-hidden="true" />
            Downloaded {downloaded}
          </span>
        )}
      </div>

      <p className="max-w-3xl text-sm text-muted-foreground">
        One row per day: the ∑: Variants TOTAL row with data up to midnight Pacific Time at the end of that day. It is
        recorded at 6:00 AM PT the next morning, so connected sources have time to sync, using this merchant's settings
        at that moment. Select a row to download that day's full Variants export (CSV), the same file Export Manager
        produces.
      </p>

      <VariantHistoryControls
        period={period}
        onPeriodChange={changePeriod}
        dateInput={dateInput}
        onDateChange={changeDate}
        onClearDate={clearDate}
        dateApplied={appliedDate !== ''}
        dateIsTooLate={dateIsTooLate}
        latest={latest}
      />

      {downloadError && (
        <div role="alert" className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {downloadError}
        </div>
      )}

      {query.isError ? (
        // No figures at all on a failed load: never a stale or recalculated table.
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
          Couldn't load Variant History. No figures are shown.
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : !data ? (
        <div role="status" className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading Variant History…
        </div>
      ) : (
        <>
          <VariantHistoryTable
            rows={data.rows}
            downloading={downloading}
            stale={query.isPlaceholderData}
            onDownload={(row) => void download(row)}
          />

          {isEmpty && (
            <p role="status" className="rounded-lg border border-dashed px-4 py-6 text-center text-sm text-muted-foreground">
              {emptyText}
            </p>
          )}

          {data.totalPages > 1 && (
            <nav aria-label="Variant History pages" className="flex flex-wrap items-center justify-center gap-1">
              <Button type="button" variant="ghost" size="sm" disabled={data.page <= 1} onClick={() => setPage((current) => Math.max(1, current - 1))}>
                Previous
              </Button>
              {pageItems(data.page, data.totalPages).map((item) =>
                typeof item === 'number' ? (
                  <Button
                    key={item}
                    type="button"
                    variant={item === data.page ? 'outline' : 'ghost'}
                    size="sm"
                    aria-current={item === data.page ? 'page' : undefined}
                    aria-label={`Page ${item}`}
                    onClick={() => setPage(item)}
                  >
                    {item}
                  </Button>
                ) : (
                  <span key={item} aria-hidden="true" className="px-2 text-muted-foreground">
                    …
                  </span>
                ),
              )}
              <Button type="button" variant="ghost" size="sm" disabled={data.page >= data.totalPages} onClick={() => setPage((current) => Math.min(data.totalPages, current + 1))}>
                Next
              </Button>
            </nav>
          )}

          <p className="text-center text-sm text-muted-foreground">
            Showing {entries} {entries === 1 ? 'entry' : 'entries'}
          </p>
        </>
      )}

      <p className="text-center text-xs text-muted-foreground">
        History starts on the day recording begins. Earlier dates are not recalculated.
      </p>
    </div>
  );
};

export default VariantHistoryTab;
